import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface ApplyToSeriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplyToThis: () => void;
  onApplyToFuture: () => void;
  isLoading?: boolean;
  /**
   * Set when this save changes the episode number. Episode numbers can't simply be copied to
   * every future instance the way a description can, so "future" means "resequence from here" —
   * say so, otherwise the choice reads as a no-op (which is exactly how it used to behave).
   */
  episodeRenumber?: { from: number | null; to: number } | null;
}

export function ApplyToSeriesDialog({
  open,
  onOpenChange,
  onApplyToThis,
  onApplyToFuture,
  isLoading = false,
  episodeRenumber = null,
}: ApplyToSeriesDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="medium">
        <DialogHeader>
          <DialogTitle>Apply changes to series?</DialogTitle>
          <DialogDescription>
            This show is part of a series. Would you like to apply your changes to only this show, or to this and all future shows in the series?
          </DialogDescription>
        </DialogHeader>

        {episodeRenumber && (
          <div className="mx-4 sm:mx-6 rounded-lg border bg-muted/40 p-4 text-sm">
            <p>
              You changed this episode from{' '}
              <span className="font-medium">{episodeRenumber.from ?? '—'}</span> to{' '}
              <span className="font-medium">{episodeRenumber.to}</span>.
            </p>
            <p className="mt-1 text-muted-foreground">
              Choosing <span className="font-medium">this and all future shows</span> will
              resequence the rest of the series from here — the next episode becomes{' '}
              {episodeRenumber.to + 1}, and so on. Choosing{' '}
              <span className="font-medium">only this show</span> leaves the later episodes on
              their current numbers.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onApplyToThis}
            disabled={isLoading}
          >
            Only this show
          </Button>
          <Button
            type="button"
            onClick={onApplyToFuture}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'This and all future shows'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
